import { useEffect, useState, useCallback } from 'react';
import Head from 'next/head';

import { bugsnag } from 'shared/utils/services/bugsnag';
import { GiftCardsPdp } from 'gift-cards/components/pdp/GiftCardsPdp';
import { GiftCardsProvider } from 'gift-cards/providers/GiftCardsProvider';
import { requestService } from 'shared/utils/services/requestService';
import { setIsFooterVisible } from 'store/slice/pageLayoutSlice';
import { usePageMetadata } from 'shared/hooks/usePageMetadata';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { useStoreDispatch } from 'store/hooks/storeHooks';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { t } from 'localization/utils/t';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';

const TP = 'pages.giftCards.index';

const GiftCardsPage = () => {
  const storeDispatch = useStoreDispatch();
  const { isMobile } = useWindowSize();
  const isMounted = useIsMounted();
  const [giftCardAmounts, setGiftCardAmounts] = useState<number[]>([]);
  const { pageTitle } = usePageMetadata({
    defaultTitle: t(`${TP}.title`, 'Buy Sneaker and Apparel Gift Cards '),
  });

  const getDenominations = useCallback(async () => {
    try {
      const { denominationsCentsList = [] } = await requestService({
        url: '/web-api/v1/gift-cards/denominations',
      });

      return denominationsCentsList.map((denomination) => denomination / 100);
    } catch (error) {
      bugsnag(error, { context: 'Error fetching Gift Card denominations' });
    }
  }, []);

  useEffect(() => {
    if (isMounted) {
      void getDenominations().then((denominations) => {
        if (denominations.length > 0) {
          setGiftCardAmounts(denominations);
        }
      });
    }

    return () => {
      storeDispatch(setIsFooterVisible(true));
    };
  }, []);

  useEffect(() => {
    if (!isMobile) {
      storeDispatch(setIsFooterVisible(false));
    } else {
      storeDispatch(setIsFooterVisible(true));
    }
  }, [isMobile]);

  return (
    <GiftCardsProvider>
      <Head>
        <title>{pageTitle}</title>
        <meta property="og:title" content={pageTitle} key="og:title" />
        <meta
          name="description"
          content={t(
            `${TP}.description`,
            'GOAT Gift Cards are available now. Use gift cards to buy the greatest products from the past, present and future. Shop brands like Nike, Air Jordan and more.',
          )}
          key="description"
        />
      </Head>
      <GiftCardsPdp giftCardAmounts={giftCardAmounts} />
    </GiftCardsProvider>
  );
};

export const getServerSideProps = async ({ locale }) => {
  return {
    props: {
      ...(await setServerSideTranslations({ locale })),
    },
  };
};

export default GiftCardsPage;
